import React from 'react'
import { SxProps } from '@mui/material'
import { KeyType, isKeyType } from '@cbgms/base/utils/keyevent'
import { useNotifier } from '@cbgms/base/notifier'
import { useTranslation } from 'react-i18next'
import IncrementDecrementButton from '@cbgms/components/src/order-parts/parts/buttons/IncrementDecrementButton'
import { useOrderButtonContext } from '@cbgms/components/src/order-parts/context/buttons/OrderButtonContext'
import { WorkorderLineState } from '@cbgms/api'
import useQuantityExceedsMaxStockWarningMessage from '../hooks/use-quantity-exceeds-max-stock-warning-message'

interface IOrderButtonUpdateOrderLineProps {
  orderLine: WorkorderLineState | undefined
  updateProductRow: (exactQuantity: number) => void
  deleteProductRow: (lineUUID: string) => void
  unitsPerSale?: number
  size?: 'small' | 'medium'
  styling?: SxProps
  disabled: boolean
  isLoading: boolean
  translationNames: 'order' | 'workorder'
  showDeleteOrder?: boolean
}

const OrderButtonUpdateOrderLine: React.FC<IOrderButtonUpdateOrderLineProps> = ({
  disabled,
  isLoading,
  size,
  styling,
  updateProductRow,
  deleteProductRow,
  orderLine,
  unitsPerSale = 1,
  translationNames,
  showDeleteOrder
}) => {
  const { t } = useTranslation()
  const notifier = useNotifier()
  const { selectedQuantity, isOpEqualOpPromotionType } = useOrderButtonContext()
  const getQuantityPostQuantityExceedsMaxStockValidation = useQuantityExceedsMaxStockWarningMessage()
  const [quantity, setQuantity] = React.useState<number>(orderLine?.Quantity || 0)

  React.useEffect(() => {
    if (selectedQuantity === undefined) {
      // update to real qty when no updates are pending
      setQuantity(orderLine?.Quantity || 0)
    } else {
      setQuantity(selectedQuantity)
    }
  }, [selectedQuantity, orderLine?.Quantity])

  const handleInputFinalizeByKey = async (event: React.KeyboardEvent<HTMLDivElement>, value: string) => {
    if (!orderLine) return
    // When the user wants to return to the initial value, he can press ESC to reset the value
    if (isKeyType(event, KeyType.EscKey)) {
      setQuantity(orderLine.Quantity)
      event.currentTarget.blur()
      return
    }
    // User can save the change by pressing the ENTER key
    if (isKeyType(event, KeyType.EnterKey)) {
      let val = parseInt(value)
      // when entering text, round upwards to a multiple of unitsPerSale
      if (val % unitsPerSale !== 0) {
        val = val + (unitsPerSale - (val % unitsPerSale))
      }

      const updatedQuantity = getQuantityPostQuantityExceedsMaxStockValidation(
        val,
        isOpEqualOpPromotionType,
        orderLine?.ExternalPart?.TotalStock || 0
      )

      setQuantity(updatedQuantity)

      if (updatedQuantity === orderLine.Quantity) {
        return
      }

      await updateProductRow(updatedQuantity)
    }
  }

  const handleInputFinalizeByBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    let val = parseInt(event.currentTarget.value)
    if (!orderLine || event.currentTarget.value === orderLine.Quantity.toString()) return

    val = getQuantityPostQuantityExceedsMaxStockValidation(val, isOpEqualOpPromotionType, orderLine?.ExternalPart?.TotalStock || 0)

    setQuantity(val)

    if (isNaN(val)) {
      event.currentTarget.value = orderLine.Quantity.toString()
      event.currentTarget.select()
      return
    }
    if (val % unitsPerSale !== 0) {
      val = val + (unitsPerSale - (val % unitsPerSale))
    }

    if (val === orderLine.Quantity) {
      return
    }

    await updateProductRow(val)
  }

  const incrementProductRow = async (exactQuantity: number) => {
    if (
      exactQuantity > 0 &&
      isOpEqualOpPromotionType &&
      orderLine &&
      exactQuantity === orderLine.Quantity + 1 &&
      exactQuantity === (orderLine?.ExternalPart?.TotalStock ?? 0)
    ) {
      notifier.warning(t('order.max-quantity-reached.toaster.title1'), t('order.max-quantity-reached.toaster.message1'))
    }

    await updateProductRow(exactQuantity)
  }

  return (
    <IncrementDecrementButton
      size={size}
      styling={styling}
      lockButtons={disabled}
      totalStock={orderLine?.ExternalPart?.TotalStock || 0}
      isLoading={isLoading}
      quantity={quantity}
      deductOrDeleteProductRow={async () => {
        if (quantity === 0) {
          return
        }
        updateProductRow(quantity - unitsPerSale)
      }}
      deleteProductRow={async () => {
        orderLine?.UUID && deleteProductRow(orderLine?.UUID)
      }}
      incrementProductRow={async () => incrementProductRow(quantity + unitsPerSale)}
      setQuantity={setQuantity}
      handleInputFinalizeByBlur={handleInputFinalizeByBlur}
      handleInputFinalizeByKey={handleInputFinalizeByKey}
      incrementQuantity={unitsPerSale}
      translationNames={translationNames}
      showDeleteOrder={showDeleteOrder}
      isOpEqualOpPromotionType={isOpEqualOpPromotionType}
    />
  )
}

export default OrderButtonUpdateOrderLine
